import { Card, CardContent } from "#components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#components/ui/select"
import { Separator } from "#components/ui/separator"
import { useAccounts } from "#hooks/use-accounts"
import { useI18n } from "#hooks/use-i18n"
import { useInvitations } from "#hooks/use-invitations"
import { cardEntrance, fadeIn, staggerContainer } from "#lib/animations"
import { AnimatePresence, motion } from "framer-motion"
import type { ReactNode } from "react"
import { useState } from "react"
import { InvitationsTable } from "./invitations-table"
import { InviteUserForm } from "./invite-user-form"

export function InvitationTab() {
  const { t } = useI18n()
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const { data: invitations = [], isLoading: isLoadingInvitations } = useInvitations(
    statusFilter === "all" ? undefined : statusFilter
  )
  const { data: accounts = [] } = useAccounts()

  // Get owned accounts for the invitation form
  const ownedAccounts = accounts.filter(
    (account) => account.meta?.pivot_role?.toLowerCase() === "owner"
  )

  // Construct invite form section
  const inviteFormSection =
    ownedAccounts.length > 0 ? (
      <motion.div variants={fadeIn}>
        <div>
          <h3 className="text-lg font-semibold mb-4">{t("pages.settings.invitation.title")}</h3>
          <InviteUserForm accounts={ownedAccounts} />
        </div>
        <Separator className="my-6" />
      </motion.div>
    ) : null

  // Construct invitations content based on state
  let invitationsContent: ReactNode

  if (isLoadingInvitations) {
    invitationsContent = (
      <motion.div
        key="loading"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="text-center py-8"
      >
        {t("pages.settings.invitation.loading")}
      </motion.div>
    )
  } else if (invitations.length === 0) {
    invitationsContent = (
      <motion.div
        key="empty"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="text-center py-8"
      >
        <p className="text-muted-foreground">{t("pages.settings.invitation.noInvitations")}</p>
      </motion.div>
    )
  } else {
    invitationsContent = (
      <motion.div
        key="table"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <InvitationsTable invitations={invitations} />
      </motion.div>
    )
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {inviteFormSection}

      <motion.div variants={fadeIn} className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{t("pages.settings.invitation.management")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("pages.settings.invitation.managementDescription")}
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("components.settings.invitation.filterByStatus")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("components.settings.invitation.allStatus")}</SelectItem>
              <SelectItem value="pending">{t("components.settings.invitation.pending")}</SelectItem>
              <SelectItem value="accepted">
                {t("components.settings.invitation.accepted")}
              </SelectItem>
              <SelectItem value="expired">{t("components.settings.invitation.expired")}</SelectItem>
              <SelectItem value="cancelled">
                {t("components.settings.invitation.cancelled")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <motion.div variants={cardEntrance}>
          <Card>
            <CardContent className="p-0">
              <AnimatePresence mode="wait">{invitationsContent}</AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </motion.div>
  )
}
